import {
  createPitchTrackerConfig,
  effectiveEventIntervalMs,
} from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';

import { voicedSamples } from './capture';

/**
 * registerTransition — 상행 프레임열에서 **성구 전환(voice break)** 지점을 세는 순수 함수.
 *
 * 결과 화면이 "고음으로 올라갈 때 소리가 N회 끊겼어요"라고 말하려면 N이 필요하다. 여기서
 * 내는 건 숫자뿐이고, 문구·표시는 소비처 몫이다.
 *
 * **무엇을 전환으로 보나**: 목소리가 흉성에서 두성으로 넘어갈 때 성대 진동 모드가 바뀌며
 * 소리가 순간 끊기고(무성·저신뢰 프레임), 다시 붙을 때 음이 훌쩍 뛴다. 그래서 판정은
 * "유성 프레임 이웃 사이의 **위로 튀는 큰 도약**"이다 — 끊김 자체가 아니라 끊김을 사이에 둔
 * 도약을 본다. 끊김만 보면 자음·숨이 전부 걸리고, 도약만 보면 빠른 슬라이드가 걸린다.
 *
 * **오검출 방어**(픽스처가 각각 하나씩 지킨다):
 *  - *글리산도*: 매끄러운 상행은 프레임당 상승이 문턱 아래다(0.3반음 수준). 문턱을 프레임
 *    단위로 두는 이유가 이것 — 총 상승폭은 글리산도가 오히려 크다.
 *  - *비브라토*: 진폭이 보통 ±1반음 안쪽이라 문턱에 못 닿고, 상행 위에 얹혀도 마찬가지다.
 *  - *숨*: 크랙의 드롭아웃은 짧다(수십 ms). 그보다 긴 공백은 숨·쉼이고, 그 사이 음이
 *    달라진 건 "끊긴" 게 아니라 새로 시작한 것이다 → 시간 상한으로 가른다.
 *  - *하행*: 위로 튄 것만 센다. 내려오며 뒤집히는 건 다른 현상(고음 이탈)이라 이 축이
 *    말할 수 있는 게 아니다.
 *
 * React·오디오 IO 의존 없음 — vitest 대상.
 */

/**
 * 전환으로 볼 최소 도약(반음). 3반음(단3도)은 사람이 의도해서 붙여 부르기 어려운 폭이라
 * 프레임 하나 만에 벌어지면 매끈한 진행이 아니다. 실제 크랙은 보통 4반음 이상 튄다.
 */
const MIN_TRANSITION_SEMITONES = 3;

/**
 * 도약이 걸쳐도 되는 최대 시간(ms). 상수를 ms가 아니라 **프레임 수**로 정의하는 이유:
 * 성대가 다시 붙는 데 걸리는 시간은 분석 창과 무관하지만, 우리가 볼 수 있는 해상도는
 * 프레임 간격(프로덕션 ≈21.3ms)이 정한다. 7프레임 ≈150ms — 크랙의 드롭아웃(대개 2~5프레임)은
 * 넉넉히 담고, 그 위는 숨·쉼으로 본다.
 */
const MAX_TRANSITION_SPAN_FRAMES = 7;
const MAX_TRANSITION_SPAN_MS =
  MAX_TRANSITION_SPAN_FRAMES *
  effectiveEventIntervalMs(createPitchTrackerConfig());

/**
 * 한 번 센 뒤 다음 전환을 받기까지의 불응 시간(ms). 크랙 하나가 여러 프레임에 걸쳐
 * 계단식으로 회복하면 경계가 둘 이상 생겨 같은 사건이 두 번 세어진다. 사람이 250ms 안에
 * 성구를 두 번 넘나드는 건 이 해상도에서 어차피 분리되지 않는다.
 */
const MIN_TRANSITION_INTERVAL_MS = 250;

/**
 * 판정에 필요한 최소 유효 프레임 수. 이보다 적으면 "끊김이 없었다"가 아니라 **볼 게 없었다**다
 * (마이크가 늦게 잡혔거나 사용자가 바로 멈춘 시도) — 0회와 구분해 측정 불가로 낸다.
 */
const MIN_VOICED_FRAMES = 5;

/**
 * 측정이 성립하려면 프레임열에 이만큼의 상승이 있어야 한다(반음). 전환 문턱과 **같은 값**인
 * 게 핵심이다: 전환이 잡혔다면 그 도약만으로도 이 값을 넘으므로, "전환 N회"와 "측정 불가"가
 * 동시에 성립할 수 없다. 지속음·하행 시도는 여기서 걸러진다 — 올라간 적이 없는 녹음은
 * "올라갈 때 끊겼나"라는 질문 자체에 답할 수 없다.
 */
const MIN_ASCENT_SEMITONES = MIN_TRANSITION_SEMITONES;

type Pitch = { semitones: number; atMs: number };

/**
 * 유효 프레임만 남긴 반음 스케일 시계열. 도약을 Hz가 아니라 반음으로 재는 이유: 같은 100Hz
 * 차이가 저음역에선 옥타브고 고음역에선 몇 반음이다. 값은 언제나 raw `f0Hz`에서 나온다 —
 * `smoothedF0Hz`는 표시 전용이고(f0frame-contract §1), 스무딩은 바로 이 도약을 깎는다.
 */
const toPitches = (frames: NativeF0Frame[]): Pitch[] =>
  // 유성 판정과 시간축 붙이기는 capture가 한다 — 같은 선을 여기서 다시 그으면 한쪽만
  // 움직였을 때 "전환 N회"와 나머지 지표가 다른 프레임을 본다.
  voicedSamples(frames).map((s) => ({
    // 기준음이 필요 없다 — 쓰는 건 차이뿐이다.
    semitones: 12 * Math.log2(s.f0Hz),
    atMs: s.tMs,
  }));

/** 프레임열이 어느 저점에서 어느 고점까지 올라갔나(반음) — 순서를 지키는 최대 상승폭. */
const maxAscentSemitones = (pitches: Pitch[]): number => {
  let lowest = pitches[0].semitones;
  let ascent = 0;
  for (const p of pitches) {
    ascent = Math.max(ascent, p.semitones - lowest);
    lowest = Math.min(lowest, p.semitones);
  }
  return ascent;
};

/**
 * 성구 전환이 일어난 시각(ms, 전환 **뒤** 첫 유성 프레임 기준)을 시간순으로. 길이가 곧 횟수다.
 *
 * `undefined`는 **측정 불가**다 — 0회(끊김 없이 잘 올라갔다)와 다른 값이니 소비처에서 섞지
 * 말 것. 조건은 두 가지: 유효 프레임 부족, 또는 상행 없음.
 */
export const detectRegisterTransitions = (
  frames: NativeF0Frame[],
): number[] | undefined => {
  const pitches = toPitches(frames);
  if (pitches.length < MIN_VOICED_FRAMES) {
    return undefined;
  }
  if (maxAscentSemitones(pitches) < MIN_ASCENT_SEMITONES) {
    return undefined;
  }

  const atMs: number[] = [];
  for (let i = 1; i < pitches.length; i += 1) {
    const from = pitches[i - 1];
    const to = pitches[i];
    const spanMs = to.atMs - from.atMs;
    const rise = to.semitones - from.semitones;
    const isBreak =
      spanMs <= MAX_TRANSITION_SPAN_MS && rise >= MIN_TRANSITION_SEMITONES;
    const isEcho =
      atMs.length > 0 &&
      to.atMs - atMs[atMs.length - 1] < MIN_TRANSITION_INTERVAL_MS;
    if (isBreak && !isEcho) {
      atMs.push(to.atMs);
    }
  }
  return atMs;
};
